import { useState } from 'react';
import { TabsEditable } from '@marriott/mi-ui-library';
import clsx from 'clsx';
import { Model } from '@adobe/aem-spa-page-model-manager';
import { EditableComponent, EditableComponentProps, ResponsiveGrid } from '@adobe/aem-react-editable-components';
import { ThemeClusterProps } from './ThemeCluster.types';
import {
  TabComponentContainer,
  Eyebrow,
  Title,
  Subtitle,
  ThemeClusterWrapper,
  CreateCardToPublishWrapper,
} from './ThemeCluster.styles';
import { CreateCardToPublish, getAEMCQItemsForProps } from './ThemeClusterAEM';

function PublishTabPanelWrapper(props: {
  selectedTab: string;
  tabDetails: { nodeName: string; tabName: string }[];
  cqItems: Record<string, object>;
}) {
  const { cqItems, selectedTab, tabDetails = [] } = props;

  return (
    <>
      {tabDetails.map<JSX.Element>(({ nodeName }) => {
        const page = cqItems[nodeName] as Model;
        const shouldShowPanel = nodeName === selectedTab;

        return (
          <CreateCardToPublishWrapper className={clsx(!shouldShowPanel && 'd-none')} key={nodeName}>
            <CreateCardToPublish page={page} />
          </CreateCardToPublishWrapper>
        );
      })}
    </>
  );
}

export const ThemeCluster = (props: ThemeClusterProps) => {
  const { eyebrow, header, pagePath = '', itemPath = '', subHeader, tabDetails, trackingProperties } = props.model;
  const cqItems = getAEMCQItemsForProps(props.model);

  const filteredTabDetails = tabDetails?.filter(({ nodeName }) => cqItems?.[nodeName]) || [];
  const tabList =
    (props.isAuthorMode ? tabDetails : filteredTabDetails)?.map(tab => ({
      tabValue: tab.nodeName,
      tabTitle: tab.tabName,
      tabSubtitle: '',
    })) || [];
  const [selectedTab, setSelectedTab] = useState<string>(tabList[0]?.tabValue);

  const onTabSelect = (selectedTabValue: string) => {
    setSelectedTab(selectedTabValue);
  };

  const customTabClickTrackValues = tabDetails?.map(
    ({ tabName }, index) => `${trackingProperties?.location}|Tab${index + 1}-${tabName}|internal`
  );

  return (
    <ThemeClusterWrapper className={props.className}>
      {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
      {header ? <Title>{header}</Title> : null}
      {subHeader ? <Subtitle>{subHeader}</Subtitle> : null}
      <TabComponentContainer>
        <TabsEditable
          clickTrackingLoc={trackingProperties?.location}
          customTabClickTrackValues={customTabClickTrackValues}
          selectedTab={selectedTab}
          setSelectedTab={(tab: string) => onTabSelect(tab)}
          tabList={tabList}
        >
          {props.isAuthorMode ? (
            tabDetails?.map(tabDetail => {
              // TabDetails is used to make sure you can author all tabs and tab panels in AEM
              return (
                <ResponsiveGrid
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  pagePath={pagePath}
                  itemPath={`${itemPath}/${tabDetail.nodeName}`}
                  columnCount="12"
                  gridClassNames={''}
                  customClassName={''}
                  config={{
                    isEmpty: () => true,
                    resourceType: 'mi-aem-common-spa/components/container',
                  }}
                />
              );
            })
          ) : (
            <PublishTabPanelWrapper cqItems={cqItems} selectedTab={selectedTab} tabDetails={filteredTabDetails} />
          )}
        </TabsEditable>
      </TabComponentContainer>
    </ThemeClusterWrapper>
  );
};

export const EditableThemeCluster = (
  props: EditableComponentProps &
    ThemeClusterProps & {
      isAuthorMode?: boolean;
    }
) => {
  const { styleclass, ...model } = props?.model || {};

  return (
    <div className={styleclass} data-target-component={model.targetComponentName || null} style={{ clear: 'both' }}>
      {props?.isAuthorMode ? (
        <EditableComponent
          config={{
            emptyLabel: 'ThemeCluster',
            isEmpty: () => false,
            resourceType: 'mi-aem-homepage-spa/components/content/themed-cluster',
          }}
          {...props}
        >
          <ThemeCluster {...props} className="container px-0" />
        </EditableComponent>
      ) : (
        <ThemeCluster {...props} className="container px-0" />
      )}
    </div>
  );
};
